import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Users, DollarSign, TrendingUp, Briefcase, GraduationCap } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroStatTile } from "@/components/shared/PageHero";
import { AdSense, LeaderboardAd, InContentAd, SidebarAd } from "@/components/shared/AdSense";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import RppToggle from "@/components/shared/RppToggle";
import CSVExportButton from "@/components/shared/CSVExportButton";
import AffiliateCta from "@/components/shared/AffiliateCta";
import OccupationLink from "@/components/shared/OccupationLink";
import { supabase } from "@/integrations/supabase/client";

const NoDegreeJobs = () => {
  const navigate = useNavigate();
  const [useRpp, setUseRpp] = useState(false);

  // Fetch national no-degree job data
  const { data: nationalData, isLoading: nationalLoading } = useQuery({
    queryKey: ['no-degree-national', useRpp],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-top-occupations', {
        body: {
          area: 'United States',
          type: 'no_degree',
          limit: 15,
          rpp: useRpp
        }
      });
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch state data for no-degree jobs
  const { data: statesData, isLoading: statesLoading } = useQuery({
    queryKey: ['no-degree-states-summary'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-state-no-degree-summary');
      
      if (error) throw error;
      return data;
    }
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Calculate summary stats from national data
  const summaryStats = nationalData?.occupations ? (() => {
    const occupations = nationalData.occupations;
    const totalJobs = occupations.reduce((sum: number, occ: unknown) => sum + (occ.total_employment || 0), 0);
    const validSalaries = occupations.filter((occ: unknown) => occ.annual_mean_wage && occ.annual_mean_wage > 0);
    const avgSalary = validSalaries.length > 0 
      ? Math.round(validSalaries.reduce((sum: number, occ: unknown) => sum + occ.annual_mean_wage, 0) / validSalaries.length)
      : 0;
    const topSalary = validSalaries.length > 0 
      ? Math.max(...validSalaries.map((occ: unknown) => occ.annual_mean_wage))
      : 0;
    
    return {
      totalJobs,
      avgSalary,
      topSalary,
      occupationCount: occupations.length
    };
  })() : null;

  const pageTitle = `Highest Paying Jobs Without a Degree (${new Date().getFullYear()}) | Wage Atlas`;
  const pageDescription = `Highest paying jobs without a degree: ${summaryStats?.occupationCount || '15'}+ careers averaging ${formatCurrency(summaryStats?.avgSalary || 45000)}. Ranked from official BLS wage data — explore by state.`;

  // SeoHead must render even while data is loading: prerender validation
  // rejects any snapshot whose <title> is still the default SPA shell.
  if (nationalLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SeoHead title={pageTitle} description={pageDescription} />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="h-32 bg-muted animate-pulse rounded" />
              <div className="h-96 bg-muted animate-pulse rounded" />
            </div>
            <div className="space-y-4">
              <div className="h-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
      />

      {/* Hero Section */}
      <PageHero
        badge={{ icon: GraduationCap, label: 'No College Degree Required' }}
        title="Highest Paying Jobs Without a Degree"
        subtitle={<>Explore {summaryStats?.occupationCount || '15'}+ careers that offer great salaries without requiring a college education</>}
      >
        {/* Top ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <LeaderboardAd className="max-w-4xl" />
        </AdFrame>

        {summaryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <HeroStatTile icon={DollarSign} value={formatCurrency(summaryStats.avgSalary)} label="Average Salary" />
            <HeroStatTile icon={TrendingUp} value={formatCurrency(summaryStats.topSalary)} label="Top Salary" />
            <HeroStatTile icon={Users} value={formatNumber(summaryStats.totalJobs)} label="Total Jobs" />
            <HeroStatTile icon={Briefcase} value={`${summaryStats.occupationCount}+`} label="Career Paths" />
          </div>
        )}
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Top No-Degree Jobs Nationally */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-xl font-semibold">
                    Top No-Degree Jobs Nationwide
                  </CardTitle>
                  <div className="flex items-center gap-2 sm:gap-3 w-full min-w-0">
                    <RppToggle
                      enabled={useRpp}
                      onToggle={setUseRpp}
                      className="min-w-0 flex-1"
                      disabled
                      disabledReason="These are national figures, and the United States is the RPP baseline (100) — real and nominal wages match. Pick a state page to compare purchasing power."
                    />
                    <CSVExportButton 
                      area="United States"
                      type="no_degree"
                      year={2025}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  High-paying careers that typically don't require a college degree
                </p>
              </CardHeader>
              <CardContent>
                {nationalData?.occupations && nationalData.occupations.length > 0 ? (
                  <SalaryTable
                    data={nationalData.occupations}
                    showAffiliateEvery={5}
                    area="United States"
                    className="shadow-none border-0"
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No data available for no-degree jobs nationwide.
                  </div>
                )}

                {/* Career advancement callout */}
                <div className="mt-8 p-6 bg-gradient-to-r from-accent/10 to-accent/5 rounded-lg border border-accent/20">
                  <div className="flex items-start gap-4">
                    <GraduationCap className="h-6 w-6 text-accent mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">Start Your Career Today</h3>
                      <p className="text-muted-foreground mb-4">
                        Many high-paying careers don't require a college degree. Find training programs, 
                        certifications, and job opportunities to get started.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <AffiliateCta
                          provider="ziprecruiter"
                          occupation="No Degree Jobs"
                          location="United States"
                          type="job-search"
                          className="bg-accent hover:bg-accent/90"
                        />
                        <AffiliateCta
                          provider="indeed"
                          occupation="No Degree Jobs"
                          location="United States"
                          type="job-search"
                          className="bg-accent hover:bg-accent/90"
                        />
                        <AffiliateCta
                          provider="coursera"
                          occupation="No Degree Jobs"
                          location="United States"
                          type="training"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                        <AffiliateCta
                          provider="udemy"
                          occupation="No Degree Jobs"
                          location="United States"
                          type="training"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                        <AffiliateCta
                          provider="topresume"
                          occupation="No Degree Jobs"
                          location="United States"
                          type="resume"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* In-content ad */}
                <InContentAd />
              </CardContent>
            </Card>

            {/* Browse by State */}
            <Card>
              <CardHeader>
                <CardTitle>Browse No-Degree Jobs by State</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Explore opportunities in your state or find the best places to relocate for your career
                </p>
              </CardHeader>
              <CardContent>
                {statesLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="h-20 bg-muted animate-pulse rounded" />
                    ))}
                  </div>
                ) : statesData?.states && statesData.states.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {statesData.states.slice(0, 10).map((state: unknown) => (
                      <Card 
                        key={state.slug}
                        className="cursor-pointer hover:shadow-md transition-shadow border-muted"
                        onClick={() => navigate(`/no-degree-jobs/${state.slug}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <MapPin className="h-5 w-5 text-primary" />
                              <div>
                                <h3 className="font-semibold">{state.area_name}</h3>
                                <p className="text-sm text-muted-foreground">{state.formatted_jobs} jobs available</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-lg">{state.formatted_salary}</p>
                              <p className="text-xs text-muted-foreground">Avg. Salary</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Unable to load state data. Please try again later.
                  </div>
                )}

                {/* View all states button */}
                <div className="mt-6 text-center">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/')}
                    className="w-full sm:w-auto"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    View All States
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Career insights */}
            <Card>
              <CardHeader>
                <CardTitle>No-Degree Career Insights</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">High-Growth Industries</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Healthcare support and home care</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Transportation and logistics</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Skilled trades and manufacturing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <span>Customer service and sales</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-wage-accent">Career Advancement Tips</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Get industry certifications and licenses</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Gain experience through apprenticeships</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Develop specialized technical skills</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Build professional networks and references</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Sidebar Ad */}
            <SidebarAd />

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Career Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start overflow-hidden"
                  onClick={() => navigate('/calculators')}
                >
                  <DollarSign className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Salary Calculator</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start overflow-hidden"
                  onClick={() => navigate('/best-states/construction-worker')}
                >
                  <TrendingUp className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Best States Comparison</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start overflow-hidden"
                  onClick={() => navigate('/trends/retail-salesperson/united-states')}
                >
                  <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Career Trends</span>
                </Button>
              </CardContent>
            </Card>

            {/* AI economy cross-link */}
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader>
                <CardTitle className="text-lg">No Degree? The AI Boom Needs You</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Data centers, power grids, and chip plants are hiring electricians, HVAC techs,
                  and line installers - no four-year degree required, with apprenticeships the
                  government is actively funding.
                </p>
                <Button
                  variant="outline"
                  className="w-full justify-start overflow-hidden"
                  onClick={() => navigate('/ai-economy-jobs')}
                >
                  <TrendingUp className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Explore AI Economy Jobs</span>
                </Button>
              </CardContent>
            </Card>

            {/* Popular Searches */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Popular Searches</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  'Construction Worker',
                  'Retail Salesperson',
                  'Security Guard',
                  'Truck Driver',
                  'Food Service Manager',
                  'Administrative Assistant'
                ].map((job) => (
                  <div key={job} className="w-full">
                    <OccupationLink 
                      occupation={{ occ_title: job }}
                      className="w-full justify-start text-sm text-muted-foreground hover:text-foreground block p-2 hover:bg-muted rounded-md transition-colors"
                    >
                      {job}
                    </OccupationLink>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoDegreeJobs;